// Category Management Utilities

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  color: string;
  createdAt: string;
  updatedAt?: string;
  order?: number;
}

/**
 * Load categories from localStorage or initialize with defaults
 */
export const loadCategories = (): Category[] => {
  try {
    const savedCategories = localStorage.getItem('admin_categories');
    if (savedCategories) {
      return JSON.parse(savedCategories);
    } else {
      // No hardcoded categories - return empty array
      // Categories should be created through admin panel
      return [];
    }
  } catch (error) {
    console.error('Error loading categories:', error);
    return [];
  }
};

/**
 * Load categories from server (LIVE mode)
 */
export const loadCategoriesFromServer = async (): Promise<Category[]> => {
  try {
    const { loadLiveCategories } = await import('./CategoryAPI');
    return await loadLiveCategories();
  } catch (error) {
    console.error('Error loading categories from server:', error);
    return []; // No fallback categories
  }
};

/**
 * Save categories to localStorage
 */
export const saveCategories = (categories: Category[]): void => {
  try {
    localStorage.setItem('admin_categories', JSON.stringify(categories));
  } catch (error) {
    console.error('Error saving categories:', error);
  }
};

/**
 * Get category options for dropdowns
 */
export const getCategoryOptions = (categories?: Category[]): { value: string; label: string; color: string }[] => {
  const cats = categories || [];
  
  // Remove duplicates by slug
  const uniqueCategories = cats.reduce((acc: Category[], current) => {
    const exists = acc.find(item => item.slug === current.slug);
    if (!exists) {
      acc.push(current);
    }
    return acc;
  }, []);
  
  return uniqueCategories.map(category => ({
    value: category.slug,
    label: category.name,
    color: category.color,
  }));
};

/**
 * Get category by slug
 */
export const getCategoryBySlug = (slug: string): Category | undefined => {
  const categories = loadCategories();
  return categories.find(category => category.slug === slug);
};

/**
 * Get category emoji based on slug (for backward compatibility)
 */
export const getCategoryEmoji = (categorySlug: string): string => {
  // Default emoji for all categories - no hardcoded mappings
  // All categories are now dynamic and managed through admin panel
  return '📷';
};

/**
 * Get all category slugs
 */
export const getCategorySlugs = (): string[] => {
  const categories = loadCategories();
  return categories.map(category => category.slug);
};

/**
 * Validate if a category slug exists
 */
export const categoryExists = (slug: string): boolean => {
  const categories = loadCategories();
  return categories.some(category => category.slug === slug);
};

export const uniqueCategorySlugs = (values: Array<string | undefined | null>): string[] => {
  const seen = new Set<string>();
  const slugs: string[] = [];
  values.forEach((value) => {
    const slug = String(value || '').trim();
    const key = slug.toLowerCase();
    if (!slug || seen.has(key)) return;
    seen.add(key);
    slugs.push(slug);
  });
  return slugs;
};

export const selectedCategorySlugs = (gallery: { category?: string; categories?: string[] }): string[] => (
  uniqueCategorySlugs([
    gallery.category,
    ...(Array.isArray(gallery.categories) ? gallery.categories : []),
  ])
);

export const categoryIsChosen = (selected: string[], slug: string): boolean => (
  selected.some((item) => item.toLowerCase() === slug.toLowerCase())
);

export const toggleCategorySlug = (selected: string[], slug: string): string[] => {
  if (categoryIsChosen(selected, slug)) {
    return selected.filter((item) => item.toLowerCase() !== slug.toLowerCase());
  }
  return selected.concat(slug);
};



